import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { NAV_INDEX } from '../config/navSearchIndex';
import { CARD_INDEX } from '../config/cardSearchIndex';
import { getTours, matchTour } from './walkthrough/tours';
import {
  MessageCircle,
  Send,
  Bot,
  User,
  Loader2,
  Trash2,
  Plus,
  History,
  ArrowRight,
  Mic,
  MicOff,
  Compass,
  Minus
} from 'lucide-react';
import { CloseIcon as XIcon } from './ui/close-icon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AIChatbot = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  // Role-filtered navigation map fed to the assistant so it can give precise,
  // always-current directions (auto-updates as NAV_INDEX/CARD_INDEX regenerate).
  const navContext = useMemo(() => {
    const role = user?.role;
    const seen = new Set();
    const lines = [];
    const add = (entry, kind) => {
      if (entry.roles && role && !entry.roles.includes(role)) return;
      const key = `${entry.label}|${entry.href}`;
      if (seen.has(key)) return;
      seen.add(key);
      lines.push(`- ${entry.label} (${kind}) → ${entry.href}`);
    };
    NAV_INDEX.forEach((e) => add(e, 'menu/page'));
    CARD_INDEX.forEach((e) => add(e, (e.keywords || '').includes('button') ? 'button' : 'section/tab'));
    return lines.join('\n').slice(0, 7500);
  }, [user?.role]);

  const isCustomer = ['customer', 'investor'].includes(user?.role);
  const quickPrompts = isCustomer
    ? ['How do I apply for a loan?', 'How do I check my loan status?', 'How do I request a Top-Up loan?', 'How do I update my profile?']
    : ['How do I add a customer?', 'How do I approve and disburse a loan?', 'Where do I add a payroll deduction?', 'How do I record an expense?'];

  const tours = useMemo(() => getTours(user?.role), [user?.role]);

  const launchTour = (tourId) => {
    setIsOpen(false);
    setTimeout(() => window.dispatchEvent(new CustomEvent('bbc:start-tour', { detail: { tourId } })), 250);
  };

  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const voiceSupported = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const toggleVoice = () => {
    if (!voiceSupported) { toast.error('Voice input is not supported on this browser'); return; }
    if (listening) { recognitionRef.current?.stop(); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.onresult = (ev) => {
      const text = ev.results[0][0].transcript;
      setMessage((prev) => (prev ? prev + ' ' : '') + text);
    };
    rec.onerror = () => { setListening(false); toast.error('Could not capture voice'); };
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    rec.start();
  };
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Minimise-to-floating-bubble state
  const [minimized, setMinimized] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [bubblePos, setBubblePos] = useState({ x: null, y: null }); // null => default bottom-right
  const dragRef = useRef({ dragging: false, moved: false, startX: 0, startY: 0, offsetX: 0, offsetY: 0 });
  const minimizedRef = useRef(false);
  const justMaximizedRef = useRef(0);
  const BUBBLE = 56;

  useEffect(() => { minimizedRef.current = minimized; }, [minimized]);

  const maximise = () => {
    justMaximizedRef.current = Date.now(); // guard against ghost-click closing the modal
    setMinimized(false);
    setHasUnread(false);
  };

  const onBubblePointerDown = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    dragRef.current = {
      dragging: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
  };
  const onBubblePointerMove = (e) => {
    const d = dragRef.current;
    if (!d.dragging) return;
    if (Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > 8) d.moved = true;
    const nx = Math.max(8, Math.min(e.clientX - d.offsetX, window.innerWidth - BUBBLE - 8));
    const ny = Math.max(8, Math.min(e.clientY - d.offsetY, window.innerHeight - BUBBLE - 8));
    setBubblePos({ x: nx, y: ny });
  };
  const onBubblePointerUp = (e) => {
    const wasMoved = dragRef.current.moved;
    dragRef.current.dragging = false;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    if (!wasMoved) { e.preventDefault(); e.stopPropagation(); maximise(); }
  };

  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !sessionId) {
      setSessionId(generateSessionId());
      setMessages([{
        role: 'assistant',
        content: "Hello! I'm your AI assistant for Better Business Credits. I can help you with questions about the software, features, and how to use the system. What would you like to know?"
      }]);
    }
  }, [isOpen, sessionId]);

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${API}/chatbot/sessions`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const loadSession = async (sid) => {
    try {
      const response = await fetch(`${API}/chatbot/history/${sid}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.history.map(h => ({
          role: h.role,
          content: h.content
        })));
        setSessionId(sid);
        setShowHistory(false);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      toast.error('Failed to load chat history');
    }
  };

  const deleteSession = async (sid) => {
    try {
      const response = await fetch(`${API}/chatbot/session/${sid}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Chat deleted');
        fetchSessions();
        if (sid === sessionId) {
          startNewChat();
        }
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const startNewChat = () => {
    setSessionId(generateSessionId());
    setMessages([{
      role: 'assistant',
      content: "Hello! I'm your AI assistant for Better Business Credits. How can I help you today?"
    }]);
    setShowHistory(false);
  };

  const sendMessage = async (e, presetText) => {
    e?.preventDefault();
    const userMessage = (presetText ?? message).trim();
    if (!userMessage || loading) return;

    setMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await fetch(`${API}/chatbot/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          nav_context: navContext
        })
      });

      if (response.ok) {
        const data = await response.json();
        const matched = matchTour(userMessage, user?.role);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          tourId: matched?.id,
          tourTitle: matched?.title
        }]);
        if (minimizedRef.current) setHasUnread(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to get response');
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "I'm sorry, I encountered an error. Please try again." 
        }]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm sorry, I couldn't connect to the server. Please try again." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const formatMessage = (content) => {
    const parts = content.split(/```/);
    
    return parts.map((part, index) => {
      if (index % 2 === 1) {
        return (
          <pre key={index} className="bg-gray-800 text-gray-100 p-2 rounded text-xs overflow-x-auto my-2">
            <code>{part}</code>
          </pre>
        );
      }
      
      const GO = /\[\[go:([^|\]]+)\|([^\]]+)\]\]/g;
      const renderGoButtons = (line) => {
        const btns = [];
        let m;
        GO.lastIndex = 0;
        while ((m = GO.exec(line)) !== null) {
          const href = m[1].trim();
          const label = m[2].trim();
          btns.push(
            <button
              key={`${href}-${btns.length}`}
              type="button"
              onClick={() => { setIsOpen(false); navigate(href); }}
              className="inline-flex items-center gap-1 mt-1 mr-2 px-2.5 py-1 rounded-full bg-forest-700 text-white text-xs hover:bg-forest-800 transition-colors"
              data-testid={`chatbot-goto${href.replace(/\//g, '-')}`}
            >
              {label} <ArrowRight className="w-3 h-3" />
            </button>
          );
        }
        return btns;
      };

      return (
        <span key={index}>
          {part.split('\n').map((rawLine, i) => {
            const goButtons = renderGoButtons(rawLine);
            let line = rawLine.replace(GO, '').trim();
            line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            const textEl = line
              ? (line.startsWith('- ') || line.startsWith('• ')
                  ? <li className="ml-4" dangerouslySetInnerHTML={{ __html: line.replace(/^[-•]\s*/, '') }} />
                  : /^\d+\.\s/.test(line)
                    ? <li className="ml-4 list-decimal" dangerouslySetInnerHTML={{ __html: line.replace(/^\d+\.\s*/, '') }} />
                    : <p className="mb-1" dangerouslySetInnerHTML={{ __html: line }} />)
              : null;
            if (!textEl && goButtons.length === 0) return <p key={i} className="mb-1">&nbsp;</p>;
            return (
              <div key={i}>
                {textEl}
                {goButtons.length > 0 && <div className="flex flex-wrap">{goButtons}</div>}
              </div>
            );
          })}
        </span>
      );
    });
  };

  if (!user) return null;

  return (
    <>
      {/* Header Icon Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => {
          setIsOpen(!isOpen);
          setMinimized(false);
          if (!isOpen) fetchSessions();
        }}
        className="relative"
        title="AI Assistant"
        data-testid="chatbot-toggle"
      >
        <MessageCircle className="w-5 h-5" style={{ color: 'var(--theme-primary)' }} />
      </Button>

      {/* Chat Modal Overlay */}
      {isOpen && !minimized && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => { if (Date.now() - justMaximizedRef.current < 600) return; setIsOpen(false); }}
          />
          
          {/* Chat Window - Centered Modal */}
          <Card className="fixed z-50 flex flex-col overflow-hidden border-0 bg-white dark:bg-gray-900 shadow-2xl rounded-2xl left-1/2 -translate-x-1/2 w-[96vw] max-w-md top-2 bottom-2 sm:top-1/2 sm:bottom-auto sm:-translate-y-1/2 sm:h-[80vh] sm:max-h-[600px]">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <Bot className="w-6 h-6" />
                <div>
                  <h3 className="font-semibold">AI Assistant</h3>
                  <p className="text-xs opacity-80">Ask me anything about the system</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowHistory(!showHistory)}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                  title="Chat History"
                >
                  <History className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={startNewChat}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                  title="New Chat"
                >
                  <Plus className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMinimized(true)}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                  title="Minimise"
                  data-testid="chatbot-minimize"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                  title="Close"
                >
                  <XIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* History Panel */}
            {showHistory && (
              <div className="absolute top-16 left-0 right-0 bottom-16 bg-white dark:bg-gray-900 z-10 overflow-y-auto border-b">
                <div className="p-3 border-b bg-gray-50 dark:bg-gray-800">
                  <h4 className="font-medium text-sm">Recent Conversations</h4>
                </div>
                {sessions.length === 0 ? (
                  <div className="p-4 text-center text-muted-foreground text-sm">
                    No previous conversations
                  </div>
                ) : (
                  <div className="divide-y">
                    {sessions.map((session) => (
                      <div
                        key={session.session_id}
                        className="p-3 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer flex items-center justify-between group"
                        onClick={() => loadSession(session.session_id)}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{session.preview}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(session.last_updated).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteSession(session.session_id);
                          }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] p-3 rounded-lg text-sm ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white rounded-br-sm'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-bl-sm'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <>
                        {formatMessage(msg.content)}
                        {msg.tourId && (
                          <button
                            type="button"
                            onClick={() => launchTour(msg.tourId)}
                            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-600 text-white text-xs font-medium hover:bg-purple-700 transition-colors"
                            data-testid="chatbot-handoff-tour"
                          >
                            <Compass className="w-3.5 h-3.5" />
                            Start the "{msg.tourTitle}" walkthrough
                          </button>
                        )}
                      </>
                    ) : msg.content}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex gap-2 justify-start">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg rounded-bl-sm">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick starter prompts (shown before the first user question) */}
            {messages.length <= 1 && !loading && (
              <div className="px-3 pb-1 flex flex-wrap gap-2" data-testid="chatbot-quick-prompts">
                {quickPrompts.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => sendMessage(null, q)}
                    className="text-xs px-3 py-1.5 rounded-full border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                    data-testid="chatbot-quick-prompt"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Guided walkthroughs (interactive on-screen tours) */}
            {messages.length <= 1 && !loading && tours.length > 0 && (
              <div className="px-3 pb-1" data-testid="chatbot-walkthroughs">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  <Compass className="w-3.5 h-3.5" />
                  Guided walkthroughs
                </div>
                <div className="flex flex-col gap-1.5">
                  {tours.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => launchTour(t.id)}
                      className="flex items-center justify-between gap-2 text-left text-xs px-3 py-2 rounded-lg border border-purple-200 text-purple-800 bg-purple-50 hover:bg-purple-100 transition-colors"
                      data-testid={`chatbot-walkthrough-${t.id}`}
                    >
                      <span>
                        <span className="font-medium block">{t.title}</span>
                        <span className="text-[11px] text-purple-500">{t.description}</span>
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <form onSubmit={sendMessage} className="p-3 border-t bg-gray-50 dark:bg-gray-800 flex-shrink-0">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your question..."
                  disabled={loading}
                  className="flex-1"
                  data-testid="chatbot-input"
                />
                {voiceSupported && (
                  <Button
                    type="button"
                    onClick={toggleVoice}
                    disabled={loading}
                    className={listening ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}
                    title={listening ? 'Stop listening' : 'Speak your question'}
                    data-testid="chatbot-voice-btn"
                  >
                    {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={loading || !message.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="chatbot-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Powered by AI
              </p>
            </form>
          </Card>
        </>
      )}

      {/* Minimised floating, draggable bubble */}
      {isOpen && minimized && (
        <button
          type="button"
          onPointerDown={onBubblePointerDown}
          onPointerMove={onBubblePointerMove}
          onPointerUp={onBubblePointerUp}
          className="fixed z-[60] rounded-full shadow-2xl bg-gradient-to-r from-blue-600 to-purple-600 text-white flex items-center justify-center touch-none cursor-grab active:cursor-grabbing hover:scale-105 transition-transform"
          style={
            bubblePos.x == null
              ? { width: BUBBLE, height: BUBBLE, right: 20, bottom: 24 }
              : { width: BUBBLE, height: BUBBLE, left: bubblePos.x, top: bubblePos.y }
          }
          title="Open AI Assistant"
          data-testid="chatbot-bubble"
        >
          <Bot className="w-6 h-6 pointer-events-none" />
          {hasUnread && (
            <span
              className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-white pointer-events-none"
              data-testid="chatbot-bubble-unread"
            />
          )}
        </button>
      )}
    </>
  );
};

export default AIChatbot;
